<template>
	<v-row class="full-height">
		<v-col class="flex-none pb-0">
			<div class="inventory-actions">
				<v-btn depressed outlined @click="prevStep()" :disabled="activeStep == 1">
					<v-icon> mdi-arrow-left </v-icon>Back
				</v-btn>
				<v-btn depressed color="accent" @click="nextStep()" :disabled="activeStep == 7">
					Next <v-icon> mdi-arrow-right </v-icon>
				</v-btn>
				<v-btn depressed outlined @click="handleCancel">Cancel</v-btn>
				<v-tooltip v-if="canManageInventory" bottom :disabled="canSave">
					<template v-slot:activator="{ on, attrs }">
						<div v-on="on" v-bind="attrs" style="display: inline-block">
							<v-btn
								depressed
								:outlined="!canSave"
								:color="canSave ? 'success' : ''"
								:disabled="!canSave"
								:loading="saving"
								@click="saveInventoryItem()"
							>Save</v-btn>
						</div>
					</template>
					<span>{{ saveTooltip }}</span>
				</v-tooltip>
			</div>

			<!-- Validation error summary banner -->
			<div v-if="saveAttempted && validationErrors.length > 0" class="inventory-error-banner">
				<v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
				<span>{{ validationErrors.join(', ') }}</span>
			</div>
		</v-col>

		<v-col cols="12" class="flex-none mb-10">
			<div class="grid-header inventory-header">
				<div class="inventory-header-status">
					<ol class="inventory-header-status-bar" :class="`step-${activeStep}`">
						<li></li>
						<li
							:class="[
								activeStep >= 1 ? 'active' : null,
								step1HasErrors ? 'has-error' : null,
							]"
							@click="nextStep(1)"
						>
							<span class="inventory-header-status-title">Item</span>
						</li>
						<li
							:class="enablePackages == false ? 'disabled' : activeStep >= 2 ? 'active' : null"
							@click="enablePackages ? nextStep(2) : null"
						>
							<span class="inventory-header-status-title">Package</span>
						</li>
						<li
							:class="enableSubItems == false ? 'disabled' : activeStep >= 3 ? 'active' : null"
							@click="enableSubItems ? nextStep(3) : null"
						>
							<span class="inventory-header-status-title">Sub-Items</span>
						</li>
						<li :class="activeStep >= 4 ? 'active' : null" @click="nextStep(4)">
							<span class="inventory-header-status-title">Inventory</span>
						</li>
						<li
							:class="enableSizing == false ? 'disabled' : activeStep >= 5 ? 'active' : null"
							@click="enableSizing ? nextStep(5) : null"
						>
							<span class="inventory-header-status-title">Sizing</span>
						</li>
						<li
							:class="enableColors == false ? 'disabled' : activeStep >= 6 ? 'active' : null"
							@click="enableColors ? nextStep(6) : null"
						>
							<span class="inventory-header-status-title">Colors</span>
						</li>
						<li
							:class="[
								activeStep >= 7 ? 'active' : null,
								step7HasErrors ? 'has-error' : null,
							]"
							@click="nextStep(7)"
						>
							<span class="inventory-header-status-title">Vendor</span>
						</li>
						<li></li>
					</ol>
				</div>
			</div>
		</v-col>

		<!-- Unsaved Changes Navigation Dialog -->
		<ConfirmNavigationDialog
			:visible="showUnsavedChangesDialog"
			:loading="savingBeforeLeave"
			@stay="handleNavigationStay"
			@leave="handleNavigationLeave"
			@save-and-leave="handleNavigationSaveAndLeave"
		/>

		<v-col v-if="activeStep == 1" cols="5" class="flex-none mx-auto">
			<div class="inventory-form">
				<div class="form-input">
					<label>TL Style Code</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="itemData.styleCode"
						:disabled="!canManageInventory"
					></v-text-field>
					<p v-if="!v$.itemData.styleCode.required.$response" class="form-error">
						* Please enter a style code
					</p>
					<p v-if="!v$.itemData.styleCode.canUse.$response" class="form-error">
						* Style code is already in use
					</p>
				</div>

				<div class="form-input">
					<label>Description</label>
					<v-text-field
						outlined
						dense
						hide-details="auto"
						v-model="itemData.description"
						:disabled="!canManageInventory"
					></v-text-field>
				</div>

				<div class="form-input">
					<label>Price</label>
					<currency-text-field
						v-model="itemData.price"
						:disabled="!canManageInventory"
						:treatAsCredit="$store.getters.allowsNegativePrices(itemData.systemCategoryId)"
					/>
				</div>

				<div class="form-input">
					<label>System Category</label>
					<v-select
						v-model="itemData.systemCategoryId"
						:items="systemCategories"
						item-text="name"
						item-value="systemCategoryId"
						dense
						outlined
						hide-details="auto"
						:disabled="!canManageInventory"
					></v-select>
					<p v-if="v$.itemData.systemCategoryId.$error" class="form-error">
						* Please select a system category
					</p>
				</div>

				<div class="inventory-form-dates">
					<div class="form-input">
						<label>Added Date</label>
						<v-menu
							v-model="showAddedDate"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="290px"
							:disabled="!canManageInventory"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="formattedAddedDate"
									append-icon="mdi-calendar"
									@click:append="showAddedDate = true"
									outlined
									dense
									v-bind="attrs"
									v-on="on"
									@blur="itemData.createDate = parseDate(formattedAddedDate)"
									hide-details="auto"
									:disabled="!canManageInventory"
								></v-text-field>
							</template>
							<v-date-picker
								color="accent"
								v-model="itemData.createDate"
								@input="showAddedDate = false"
							></v-date-picker>
						</v-menu>
					</div>

					<div class="form-input">
						<label>Inactive Date</label>
						<v-menu
							v-model="showInactiveDate"
							:close-on-content-click="false"
							transition="scale-transition"
							offset-y
							min-width="290px"
							:disabled="!canManageInventory"
						>
							<template v-slot:activator="{ on, attrs }">
								<v-text-field
									v-model="formattedInactiveDate"
									append-icon="mdi-calendar"
									@click:append="showInactiveDate = true"
									outlined
									dense
									v-bind="attrs"
									v-on="on"
									@blur="itemData.inactiveDate = parseDate(formattedInactiveDate)"
									hide-details="auto"
									:disabled="!canManageInventory"
								></v-text-field>
							</template>
							<v-date-picker
								color="accent"
								v-model="itemData.inactiveDate"
								@input="showInactiveDate = false"
							></v-date-picker>
						</v-menu>
					</div>
				</div>
			</div>
		</v-col>

		<InventoryItemPackage
			v-else-if="activeStep == 2"
			:item="itemData"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
		/>

		<InventoryItemSubItems
			v-else-if="activeStep == 3"
			:item="itemData"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
		/>

		<InventoryItemInformation
			v-else-if="activeStep == 4"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
		/>

		<InventoryItemSizing
			v-else-if="activeStep == 5"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
		/>

		<InventoryItemColors
			v-else-if="activeStep == 6"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
		/>

		<InventoryItemVendors
			v-else-if="activeStep == 7"
			:canManageInventory="canManageInventory"
			@updated="updateItemData"
			:validation="v$"
		/>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { GET_ANY_ITEM_BY_ID } from "@/gql/inventory";
import useVuelidate from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { defineComponent } from "vue";
import { cloneDeep, isEqual } from "lodash";
import InventoryItemPackage from "@/components/Inventory/InventoryItemPackage.vue";
import InventoryItemSubItems from "@/components/Inventory/InventoryItemSubItems.vue";
import InventoryItemInformation from "@/components/Inventory/InventoryItemInformation.vue";
import InventoryItemSizing from "@/components/Inventory/InventoryItemSizing.vue";
import InventoryItemColors from "@/components/Inventory/InventoryItemColors.vue";
import InventoryItemVendors from "@/components/Inventory/InventoryItemVendors.vue";
import ConfirmNavigationDialog from "@/components/ConfirmNavigationDialog.vue";
import { renderPrice } from "@/helpers/PricingCalculator";
import { hasPermission, SystemPermissions } from "@/models/User";
import { getApiUrl } from "@/helpers";

export default defineComponent({
	name: "InventoryItemView",
	components: {
		InventoryItemPackage,
		InventoryItemSubItems,
		InventoryItemInformation,
		InventoryItemSizing,
		InventoryItemColors,
		InventoryItemVendors,
		ConfirmNavigationDialog,
	},
	props: {
		inventoryItemId: {
			type: Number,
			default: null,
		},
		activeStep: {
			type: Number,
			default: 1,
		},
	},
	setup() {
		return { v$: useVuelidate() };
	},
	data() {
		return {
			showInactiveDate: false,
			formattedInactiveDate: "",
			showAddedDate: false,
			formattedAddedDate: this.formatDate(
				new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)
			),
			itemData: {
				inventoryItemId: null,
				systemCategoryId: null,
				styleCode: "",
				description: "",
				inactiveDate: null,
				createDate: "",
				price: 0.0,
				packageItems: [],
				subItems: [],
				dropShip: false,
				packingList: false,
				stockItems: false,
				infiniteInventory: false,
				processingId: null,
				sizeChart: {
					templateSizeChartId: null,
					name: "",
					description: "",
					includeLengths: false,
					sizes: [],
				},
				colorOptions: [
					{
						name: "",
						description: "",
						position: 1,
						colorPalettes: [],
						type: "color",
					},
				],
				vendorId: null,
				vendorPartNumber: null,
				vendorCost: 0.0,
				leadTime: 0,
			},
			systemCategories: [],
			packageItemsView: false,
			subItemView: false,
			inventoryInfoView: false,
			sizeView: false,
			colorView: false,
			vendorInfoView: false,
			saveAttempted: false,
			saving: false,
			// Unsaved changes guard state
			initialState: null,
			showNavigationWarning: true,
			showUnsavedChangesDialog: false,
			pendingNavigation: null,
			savingBeforeLeave: false,
		};
	},
	validations() {
		return {
			itemData: {
				styleCode: {
					required,
					canUse: helpers.withAsync(async (value) => {
						const ableToUse = await this.styleCodeCanBeUsed(value);
						return ableToUse;
					}),
				}, //equivalent to this.itemData.styleCode
				systemCategoryId: { required },
				vendorId: { required },
			},
		};
	},
	methods: {
		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		parseDate(date) {
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		renderPrice,

		renderLengthString(lengthsArr) {
			let activeLengths = lengthsArr.filter((l) => !l.isRetired);
			let lengthString = "";
			//loop through length array and get each size and compile into one string
			activeLengths.forEach((len, i) => {
				if (i !== activeLengths.length - 1) {
					lengthString += `${len.length}, `;
				} else {
					lengthString += `${len.length}`;
				}
			});

			return lengthString;
		},

		async nextStep(step) {
			//check for validation errors for step 1 items
			if (this.activeStep == 1) {
				await this.v$.itemData.styleCode.$touch();
				await this.v$.itemData.systemCategoryId.$touch();

				if (this.v$.itemData.styleCode.$error || this.v$.itemData.systemCategoryId.$error) return;
			}

			//if step is passed in switch to that step ex:user clicks a step instead of hitting next
			let nextStep;
			if (step) {
				nextStep = step;
			} else {
				switch (this.activeStep) {
					case 1:
						this.$store.dispatch("updateInventoryItem", this.itemData);
						//if user assigns package category switch to package view otherwise skip to sub item view
						//unless subitems arent allowed then skip to inventory
						nextStep = this.enablePackages ? 2 : this.enableSubItems ? 3 : 4;
						break;
					case 2:
						//skip subItem view since we are working with a package
						nextStep = this.enableSubItems ? 3 : 4;
						break;
					case 4:
						//skip sizing or colors if the category selected does not allow those steps
						nextStep = this.enableSizing ? 5 : this.enableColors ? 6 : 7;
						break;
					default:
						nextStep = this.activeStep == 7 ? null : this.activeStep + 1;
				}
			}

			this.navigate(nextStep);
		},

		async prevStep() {
			let prevStep;
			switch (this.activeStep) {
				case 3:
					prevStep = this.itemData.systemCategoryId == null ? 2 : 1;
					break;
				case 4:
					//check if sub items or packages are allowed
					prevStep = this.enableSubItems ? 3 : this.enablePackages ? 2 : 1;
					break;
				case 6:
					//check if sizing is allowed
					prevStep = this.enableSizing ? 5 : 4;
					break;
				case 7:
					//check if sizing or colors are allowed otherwise skip to inventory step
					prevStep = this.enableColors ? 6 : this.enableSizing ? 5 : 4;
					break;
				default:
					prevStep = this.activeStep == 1 ? null : (this.activeStep -= 1);
			}

			this.navigate(prevStep);
		},

		navigate(step) {
			// Store current state before navigation
			this.$store.dispatch("globalState/saveInventoryItemsViewState", {
				activeStep: step,
				item: this.itemData,
				lastSavedState: this.$store.getters["globalState/inventoryItemsViewState"]?.lastSavedState,
			});

			// Navigate to appropriate route
			const path = this.getPathForStep(step);
			if (path) {
				this.$router.push(path);
			}
		},

		getPathForStep(step) {
			const base = this.inventoryItemId
				? `/inventory/items/${this.inventoryItemId}`
				: "/inventory/items/new";

			switch (step) {
				case 1:
					return base;
				case 2:
					return `${base}/package`;
				case 3:
					return `${base}/subitems`;
				case 4:
					return `${base}/inventory`;
				case 5:
					return `${base}/sizing`;
				case 6:
					return `${base}/colors`;
				case 7:
					return `${base}/vendor`;
				default:
					return base;
			}
		},

		updateItemData() {
			this.itemData = this.$store.getters.inventoryItem;
		},

		async saveInventoryItem() {
			this.saveAttempted = true;

			//check for validation errors and navigate to the error step
			await this.v$.$validate();

			if (this.v$.itemData.styleCode.$error || this.v$.itemData.systemCategoryId.$error) {
				this.navigate(1);
				this.$toast.error("Please complete all required fields on the Item step.");
				return;
			} else if (this.v$.itemData.vendorId.$error) {
				this.navigate(7);
				this.$toast.error("Please select a vendor before saving.");
				return;
			}

			this.saving = true;

			try {
				let inventoryInfo = {
					info: {
						vendorId: this.itemData.vendorId,
						styleCode: this.itemData.styleCode,
						description: this.itemData.description,
						cost: this.itemData.vendorCost,
						leadTime: this.itemData.leadTime || 0,
						price: this.itemData.price,
						systemCategoryId: this.itemData.systemCategoryId,
						vendorPartNumber: this.itemData.vendorPartNumber,
						inactiveDate: this.itemData.inactiveDate,
						infiniteInventory: this.itemData.infiniteInventory,
						dropShip: this.itemData.dropShip,
						packingList: this.itemData.packingList,
						stockItems: this.itemData.stockItems,
						processTypeId: this.itemData.processTypeId,
					},
				};

				let res;
				if (this.itemData.inventoryItemId !== null) {
					//figure out the item type we are updating and use the appropriate endpoint
					if (this.enablePackages) {
						let packageItem = {
							...inventoryInfo,
							packageId: this.itemData.inventoryItemId,
							items: this.itemData.packageItems,
						};

						res = await this.$http.put(
							getApiUrl("/api/inventory/packages"),
							packageItem
						);
					} else if (this.enableSubItems) {
						let genericItem = {
							...inventoryInfo,
							inventoryItemId: this.itemData.inventoryItemId,
							phantomSubItems: this.itemData.subItems
								? this.itemData.subItems.flatMap((obj) => {
										//use flatMap to only append items that are subitem categories
										if (obj.subItemCategoryId) {
											const subItem = {
												subItemId: obj.subItemCategoryId,
											};
											if (obj.quantity) {
												subItem.quantity = obj.quantity;
											}
											if (obj.isDefault) {
												subItem.isDefault = obj.isDefault;
											}
											if (obj.priceIncluded) {
												subItem.priceIncluded = obj.priceIncluded;
											}
											if (obj.braidPosition) {
												subItem.braidPosition = obj.braidPosition;
											}
											return subItem;
										} else {
											return [];
										}
								  })
								: [],
							subItems: this.itemData.subItems
								? this.itemData.subItems.flatMap((obj) => {
										//use flatMap to only append items that are individual subitems
										if (obj.inventoryItemId) {
											return {
												subItemId: obj.inventoryItemId,
												quantity: obj.quantity,
												isDefault: obj.isDefault,
												priceIncluded: obj.priceIncluded,
												braidPosition: obj.braidPosition,
											};
										} else {
											return [];
										}
								  })
								: [],
							sizeChart: this.itemData?.sizeChart
								? {
										templateSizeChartId: this.itemData.sizeChart?.templateSizeChartId,
										name: this.itemData.sizeChart?.name,
										description: this.itemData.sizeChart?.description,
										includeLengths: this.itemData.sizeChart?.includeLengths,
										sizes: this.itemData.sizeChart?.sizes?.map((obj) => {
											const sizeObj = {
												name: obj.name,
												showSize: obj.showSize,
												lengths: this.itemData.sizeChart?.includeLengths ? obj.lengths : "",
												sizeId: obj.lengths.length > 0 ? obj.lengths[0].sizeId : null,
												isRetired: obj.isRetired,
												sequence: obj.sequence,
											};
											return sizeObj;
										}),
								  }
								: null,
							colorOptions: this.itemData.colorOptions,
						};

						res = await this.$http.put(
							getApiUrl("/api/inventory/items"),
							genericItem
						);
					} else {
						let subItem = {
							...inventoryInfo,
							subItemId: this.itemData.inventoryItemId,
							colorOptions: this.itemData.colorOptions,
						};

						res = await this.$http.put(
							getApiUrl("/api/inventory/subItems"),
							subItem
						);
					}
				} else {
					//figure out the item type we are saving and use the appropriate endpoint
					if (this.enablePackages) {
						let packageItem = {
							...inventoryInfo,
							items: this.itemData.packageItems,
						};

						res = await this.$http.post(
							getApiUrl("/api/inventory/packages"),
							packageItem
						);
					} else if (this.enableSubItems) {
						let genericItem = {
							...inventoryInfo,
							phantomSubItems: this.itemData.subItems.flatMap((obj) => {
								//use flatMap to only append items that are subitem categories
								if (obj.subItemCategoryId) {
									return {
										subItemId: obj.subItemCategoryId,
										quantity: obj.quantity,
										isDefault: obj.isDefault,
										priceIncluded: obj.priceIncluded,
										braidPosition: obj.braidPosition,
									};
								} else {
									return [];
								}
							}),
							subItems: this.itemData.subItems.flatMap((obj) => {
								//use flatMap to only append items that are individual subitems
								if (obj.inventoryItemId) {
									return {
										subItemId: obj.inventoryItemId,
										quantity: obj.quantity,
										isDefault: obj.isDefault,
										priceIncluded: obj.priceIncluded,
										braidPosition: obj.braidPosition,
									};
								} else {
									return [];
								}
							}),
							sizeChart: this.getSizeChartOptions(),
							colorOptions: this.itemData.colorOptions,
						};

						res = await this.$http.post(
							getApiUrl("/api/inventory/items"),
							genericItem
						);
					} else {
						let subItem = {
							...inventoryInfo,
							subItemId: this.itemData.inventoryItemId,
							colorOptions: this.itemData.colorOptions,
						};

						res = await this.$http.post(
							getApiUrl("/api/inventory/subItems"),
							subItem
						);
					}
				}

				this.$toast.success("Inventory item saved successfully.");
				this.showNavigationWarning = false;
				this.handleCancel();
			} catch (error) {
				const message =
					error?.response?.data?.error ||
					error?.response?.data?.title ||
					"An error occurred while saving. Please try again.";
				this.$toast.error(message);
			} finally {
				this.saving = false;
			}
		},

		getSizeChartOptions() {
			let chart = {
				templateSizeChartId: this.itemData.sizeChart.templateSizeChartId,
				name: this.itemData.sizeChart.name,
				description: this.itemData.sizeChart.description,
				includeLengths: this.itemData.sizeChart.includeLengths,
				sizes: this.itemData.sizeChart.sizes.map((obj) => {
					const sizeObj = {
						name: obj.name,
						showSize: obj.showSize,
						lengths: obj.lengths,
						isRetired: obj.isRetired,
						sequence: obj.sequence,
					};
					return sizeObj;
				}),
			};

			// make sure we don't send an empty size chart
			// https://github.com/sparkfish/tl-oes/issues/58
			if (chart.name === "" && chart.description === "" && chart.sizes.length === 0)
				return undefined;

			return chart;
		},

		async styleCodeCanBeUsed() {
			const owner = await this.$catalog.getStyleCodeOwner(this.itemData.styleCode);

			// if the style code is not in use, or if it is in use by the current item, then it is valid
			return owner === null || owner.inventoryItemId === this.itemData.inventoryItemId;
		},

		async setInventoryByType(item) {
			//check what kind of item was selected and get that inventory items data by id
			// Use itemType if available (from getAnyInventoryItem), otherwise fall back to systemCategory checks
			if (item.itemType) {
				if (item.itemType === 'PACKAGE') {
					await this.getInventoryPackage(item.itemId);
				} else if (item.itemType === 'SUB_ITEM') {
					await this.getInventorySubItem(item.itemId);
				} else {
					await this.getInventoryItem(item.itemId);
				}
			} else if (this.$systemCategories.isPackage(item.systemCategoryId)) {
				//get inventory package by id
				await this.getInventoryPackage(item.itemId);
			} else if (this.$systemCategories.isSubItem(item.systemCategoryId)) {
				//get inventory subItem by id
				await this.getInventorySubItem(item.itemId);
			} else {
				//get regular inventory items by id
				await this.getInventoryItem(item.itemId);
			}
		},

		async getAnyInventoryItem(itemId) {
			let response = {};

			try {
				const res = await this.$apollo.query({
					query: GET_ANY_ITEM_BY_ID,
					variables: {
						id: itemId,
					},
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory item", res);
				return res.data.inventoryItem;
			} catch (error) {
				console.log(error);
			}

			return response;
		},

		async getInventoryItem(itemId) {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
				        inventoryItemById(id: ${itemId}) {
				            inventoryItemId
                            vendorId
                            styleCode
                            description
                            vendorCost
                            leadTime
                            price
                            systemCategoryId
                            vendorPartNumber
                            inactiveDate
                            infiniteInventory
                            dropShip
                            packingList
							processTypeId
                            stockItems
                            createDate
                            phantomSubItems {
								subItemCategoryId: phantomSubItemId
								quantity
								isDefault
								priceIncluded
								braidPosition
                                itemData: subItemCategory {
                                    systemCategoryId
                                    styleCode: name
                                    description
                                }
							}
                            subItems {
                                inventoryItemId: subItemId
                                quantity
                                isDefault
                                priceIncluded
								braidPosition
                                itemData {
                                    systemCategoryId
                                    styleCode
                                    description
                                }
                            }
                            colorOptions {
                                colorOptionId
                                name
                                description
                                position
								type
                                colorPalettes {
									colorPaletteId
                                    name
                                    description
                                    upcharge
									templateColorPaletteId
                                    colors {
										colorId
                                        label
                                        description
                                        hexCode
                                        isVisible
										isRetired
										isCustomColor
                                    }
                                }
                            }
                            sizeChart {
                                name
                                description
                                includeLengths
								templateSizeChartId
                                sizes {
                                    lengths {
										length
										sizeId
										sizeLengthId
										isRetired
									}
                                    name
                                    showSize
                                    sizeId
									isRetired
									sequence
                                }
                            }
				        }
				    }`,
					fetchPolicy: "no-cache",
				});

				console.log("getting inventory item", res);
				//change gql response so that phantom items merge with subitems
				this.itemData = {
					...res.data.inventoryItemById,
					subItems: [
						...res.data.inventoryItemById.phantomSubItems,
						...res.data.inventoryItemById.subItems,
					],
				};
			} catch (error) {
				console.log(error);
			}
		},

		async getInventoryPackage(itemId) {
			try {
				const res = await this.$apollo.query({
					query: gql`query {
						packageById(id: ${itemId}) {
							inventoryItemId
							vendorId
							styleCode
							description
							vendorCost
							leadTime
							price
							systemCategoryId
							vendorPartNumber
							inactiveDate
							infiniteInventory
							dropShip
							packingList
							processTypeId
							stockItems
							createDate
							packageItems {
								id
								parentItemId
								packageCategoryId
								quantity
								isDefault
								priceIncluded
								packageId
								packageCategory {
									packageCategoryId
									name
									description
									systemCategoryId
									inventoryItemId
									inventoryItem {
										inventoryItemId
										systemCategoryId
										styleCode
										description
										type
									}
								}
								subItems {
									id
									parentItemId
									packageCategoryId
									packageCategory {
										packageCategoryId
										name
										description
										systemCategoryId
										inventoryItemId
										inventoryItem {
											inventoryItemId
											systemCategoryId
											styleCode
											description
											type
										}
									}
									quantity
									isDefault
									priceIncluded
									packageId
									inventoryItemId
									itemData: inventoryItem {
										inventoryItemId
										systemCategoryId
										styleCode
										description
										type
									}
									subItemCategoryId
									subItemCategory {
										name
										description
										systemCategoryId
									}
									braidPosition
								}
							}
						}
					}`,
					fetchPolicy: "no-cache",
				});
				console.log("getting inventory package", res);

				let inventoryPackage = res.data.packageById;

				// Iterate over packageItems to directly update each item based on subItems
				inventoryPackage.packageItems.forEach((packageItem) => {
					if (
						!packageItem.packageCategory.description ||
						packageItem.packageCategory.description.trim() === ""
					) {
						packageItem.packageCategory.description =
							packageItem.packageCategory.inventoryItem.description;
					}

					packageItem.subItems.forEach(async (subItem) => {
						// Check if subItemCategoryId exists and override packageCategory with subItemCategory values
						if (subItem.subItemCategoryId && subItem.subItemCategory) {
							subItem.info = {
								...subItem.packageCategory,
								systemCategoryId: subItem.subItemCategory.systemCategoryId,
								name: subItem.subItemCategory.name,
								description: subItem.subItemCategory.description,
							};
						} else if (subItem.inventoryItemId) {
							let item = await this.getAnyInventoryItem(subItem.inventoryItemId);
							subItem.info = {
								...item,
								systemCategoryId: item.systemCategoryId,
								name: item.styleCode,
								description: item.description,
							};
						} else if (subItem.packageCategoryId) {
							subItem.info = {
								...subItem.packageCategory,
							};
						}
					});
				});

				this.itemData = inventoryPackage;
			} catch (error) {
				console.log(error);
			}
		},

		async getInventorySubItem(itemId) {
			try {
				const res = await this.$apollo.query({
					query: gql`
						query GetSubItemById($id: Int!) {
							subItemById(id: $id) {
								inventoryItemId
								vendorId
								styleCode
								description
								vendorCost
								leadTime
								price
								systemCategoryId
								vendorPartNumber
								inactiveDate
								infiniteInventory
								dropShip
								packingList
								processTypeId
								stockItems
								createDate
								colorOptions {
									colorOptionId
									name
									description
									position
									type
									colorPalettes {
										colorPaletteId
										name
										description
										upcharge
										templateColorPaletteId
										colors {
											colorId
											label
											description
											hexCode
											isVisible
											isRetired
											isCustomColor
										}
									}
								}
							}
						}
					`,
					fetchPolicy: "no-cache",
					variables: {
						id: itemId,
					},
				});

				console.log("getting inventory subItem", res);
				this.itemData = res.data.subItemById;
			} catch (error) {
				console.log(error);
			}
		},

		// --- Unsaved Changes Guard Methods ---
		getTrackableState() {
			return { itemData: this.itemData };
		},
		captureInitialState() {
			this.initialState = cloneDeep(this.getTrackableState());
		},
		async confirmNavigation() {
			return new Promise((resolve) => {
				this.pendingNavigation = resolve;
				this.showUnsavedChangesDialog = true;
			});
		},
		handleNavigationStay() {
			this.showUnsavedChangesDialog = false;
			if (this.pendingNavigation) {
				this.pendingNavigation(false);
				this.pendingNavigation = null;
			}
		},
		handleNavigationLeave() {
			this.showUnsavedChangesDialog = false;
			this.showNavigationWarning = false;
			if (this.pendingNavigation) {
				this.pendingNavigation(true);
				this.pendingNavigation = null;
			}
		},
		async handleNavigationSaveAndLeave() {
			this.savingBeforeLeave = true;
			try {
				await this.saveInventoryItem();
				// saveInventoryItem sets showNavigationWarning = false on success
				if (!this.showNavigationWarning) {
					this.showUnsavedChangesDialog = false;
					if (this.pendingNavigation) {
						this.pendingNavigation(true);
						this.pendingNavigation = null;
					}
				} else {
					// Save failed (validation) — stay on page
					this.showUnsavedChangesDialog = false;
					if (this.pendingNavigation) {
						this.pendingNavigation(false);
						this.pendingNavigation = null;
					}
				}
			} catch {
				this.showUnsavedChangesDialog = false;
				if (this.pendingNavigation) {
					this.pendingNavigation(false);
					this.pendingNavigation = null;
				}
			} finally {
				this.savingBeforeLeave = false;
			}
		},
		isInventoryItemStepNavigation(from, to) {
			const fromPath = from.path;
			const toPath = to.path;

			// Match /inventory/items/new or /inventory/items/:id
			const itemMatch = fromPath.match(/^\/inventory\/items\/(new|\d+)/);
			if (itemMatch) {
				const itemId = itemMatch[1];
				return toPath.startsWith(`/inventory/items/${itemId}`);
			}
			return false;
		},
		handleBeforeUnload(event) {
			if (this.hasUnsavedChanges && this.showNavigationWarning) {
				event.preventDefault();
				event.returnValue = "";
			}
		},

		handleCancel() {
			// Disable unsaved changes guard for this navigation
			this.showNavigationWarning = false;

			// Clear view state
			this.$store.dispatch("globalState/saveInventoryItemsViewState", {
				activeStep: 1,
				item: null,
				lastSavedState: null,
			});

			// Navigate back to list
			this.$router.push("/inventory/items");
		},
	},
	computed: {
		systemCategoriesLoaded() {
			return this.systemCategories && this.systemCategories.length > 0;
		},
		enablePackages() {
			// Wait for system categories to load
			if (!this.systemCategoriesLoaded) return false;

			if (
				this.itemData.systemCategoryId === null ||
				this.$systemCategories.isPackage(this.itemData.systemCategoryId)
			) {
				return true;
			} else {
				return false;
			}
		},
		enableSubItems() {
			// Wait for system categories to load
			if (!this.systemCategoriesLoaded) return false;
			return this.$systemCategories.allowsSubItems(this.itemData.systemCategoryId);
		},
		enableSizing() {
			// Wait for system categories to load
			if (!this.systemCategoriesLoaded) return false;
			return this.$systemCategories.allowsSizes(this.itemData.systemCategoryId);
		},
		enableColors() {
			// Wait for system categories to load
			if (!this.systemCategoriesLoaded) return false;
			return this.$systemCategories.allowsColors(this.itemData.systemCategoryId);
		},
		canManageInventory() {
			const user = this.$store.getters.userInfo;
			return (
				hasPermission(user, SystemPermissions.Inventory_InventoryItems_Create_Update_Delete) ||
				user.isAdmin
			);
		},
		canSave() {
			return (
				!!this.itemData.styleCode &&
				this.itemData.systemCategoryId != null &&
				this.itemData.vendorId != null
			);
		},
		saveTooltip() {
			const missing = [];
			if (!this.itemData.styleCode) missing.push("Style Code");
			if (this.itemData.systemCategoryId == null) missing.push("System Category");
			if (this.itemData.vendorId == null) missing.push("Vendor");
			return missing.length > 0
				? `Required: ${missing.join(", ")}`
				: "";
		},
		validationErrors() {
			const errors = [];
			if (!this.itemData.styleCode) errors.push("Style Code is required");
			if (this.itemData.systemCategoryId == null) errors.push("System Category is required");
			if (this.itemData.vendorId == null) errors.push("Vendor is required");
			return errors;
		},
		step1HasErrors() {
			return (
				this.saveAttempted &&
				(!this.itemData.styleCode || this.itemData.systemCategoryId == null)
			);
		},
		step7HasErrors() {
			return this.saveAttempted && this.itemData.vendorId == null;
		},
		hasUnsavedChanges() {
			if (!this.initialState) return false;
			const currentState = this.getTrackableState();
			return !isEqual(currentState, this.initialState);
		},
	},
	watch: {
		"itemData.inactiveDate"(value) {
			this.formattedInactiveDate = this.formatDate(value);
		},
		"itemData.createDate"(value) {
			this.formattedAddedDate = this.formatDate(value);
		},
		// Watch for system categories to load from store
		"$store.state.systemCategories.systemCategories": {
			handler(newValue) {
				if (newValue && Array.isArray(newValue) && newValue.length > 0) {
					this.systemCategories = this.$systemCategories.systemCategories;
				}
			},
			immediate: true,
		},
	},
	async beforeRouteLeave(to, from, next) {
		// Allow navigation between inventory item steps (same entity)
		if (this.isInventoryItemStepNavigation(from, to)) {
			next();
			return;
		}

		// If no unsaved changes or warning disabled, allow navigation
		if (!this.hasUnsavedChanges || !this.showNavigationWarning) {
			next();
			return;
		}

		// Show confirmation dialog
		const shouldProceed = await this.confirmNavigation();
		if (shouldProceed) {
			next();
		} else {
			next(false);
		}
	},
	async mounted() {
		// Add browser close warning
		window.addEventListener("beforeunload", this.handleBeforeUnload);

		// Wait for system categories to load if not already available
		if (!this.$systemCategories.systemCategories || this.$systemCategories.systemCategories.length === 0) {
			// System categories not loaded yet, load them
			await this.$store.dispatch("retrievesystemCategories", this);
			this.$systemCategories.load(this);
		}
		this.systemCategories = this.$systemCategories.systemCategories;

		const item = await this.getAnyInventoryItem(this.inventoryItemId);
		//check if item data is passed in and assign to components data and to vuex state
		if (item) {
			await this.setInventoryByType(item);

			//strip time from date inputs for v-date-picker
			this.itemData.createDate = this.itemData.createDate.substr(0, 10);
			this.itemData.inactiveDate
				? (this.itemData.inactiveDate = this.itemData.inactiveDate.substr(0, 10))
				: null;

			//if sizeChart data exists reformat the sizeChart data size lengths to work as strings
			if (this.itemData.sizeChart && this.itemData.sizeChart.name) {
				this.itemData.sizeChart = {
					...this.itemData.sizeChart,
					sizes: this.itemData.sizeChart.sizes
						.map((obj) => {
							let sizeObj = {
								sizeId: obj.lengths.length > 0 ? obj.lengths[0].sizeId : null,
								name: obj.name,
								showSize: obj.showSize,
								lengths: this.renderLengthString(obj.lengths),
								isRetired: obj.isRetired,
								sequence: obj.sequence,
							};
							return sizeObj;
						})
						.sort((a, b) => (a.sequence ?? 1000) - (b.sequence ?? 1000)),
				};
			}
		}

		this.$store.dispatch("newInventoryItem", this.itemData);
		await this.v$.itemData.styleCode.$touch();
		await this.v$.itemData.systemCategoryId.$touch();

		// Capture initial state for unsaved changes detection
		this.$nextTick(() => this.captureInitialState());
	},
	beforeDestroy() {
		// Clean up
		window.removeEventListener("beforeunload", this.handleBeforeUnload);
		if (this.v$) {
			this.v$.$reset();
		}
	},
});
</script>

<style lang="scss" scoped>
.inventory {
	&-actions {
		display: flex;
		justify-content: flex-end;
		align-items: center;

		.v-btn {
			width: 8rem;
			background-color: $color-white;
			border-color: $color-gray;
			margin: 0.4rem;

			.v-icon {
				font-size: 1.8rem;
			}

			&.success {
				background-color: $color-green !important;
				border-color: $color-green !important;
				color: $color-white !important;

				&.v-btn--disabled {
					background-color: $color-white !important;
					border-color: $color-gray !important;
					color: $color-gray !important;
				}
			}
		}
	}

	&-error-banner {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		padding: 0.6rem 1rem;
		font-size: 1.3rem;
		color: $color-primary;
	}

	&-header {
		position: relative;
		grid-template-columns: 1fr 60% 1fr;

		&-status {
			grid-column: 2;
			width: 100%;
			font-size: 1.2rem;

			&-bar {
				list-style: none;
				padding: 0;
				display: flex;
				width: 100%;
				z-index: 1;
				position: relative;
				flex-direction: row;
				align-items: stretch;
				justify-content: space-between;

				&:before {
					content: "";
					position: absolute;
					width: 86%;
					background-color: $color-tertiary;
					height: 10px;
					border-radius: 25px;
					left: 7%;
					top: 50%;
					transform: translateY(-50%);
					transition-delay: 2s;
					transition: width 0.5s linear;
					z-index: 2;
				}

				&:after {
					content: "";
					position: absolute;
					width: 86%;
					background-color: $color-gray;
					height: 10px;
					border-radius: 25px;
					left: 50%;
					top: 50%;
					will-change: transform;
					transform: translate(-50%, -50%);
					transition: width 0.9s ease-in-out;
					z-index: 1;
				}

				&.step-1 {
					&:before {
						width: 7.5%;
					}
				}

				&.step-2 {
					&:before {
						width: 19.25%;
					}
				}

				&.step-3 {
					&:before {
						width: 31.25%;
					}
				}

				&.step-4 {
					&:before {
						width: 43.25%;
					}
				}

				&.step-5 {
					&:before {
						width: 55.25%;
					}
				}

				&.step-6 {
					&:before {
						width: 67.25%;
					}
				}

				&.step-7 {
					&:before {
						width: 79.25%;
					}
				}

				li {
					display: flex;
					position: relative;
					height: 50px;
					cursor: pointer;
					flex-basis: 38px;

					&:before {
						content: "";
						z-index: 6;
						background-color: $color-white;
						position: absolute;
						top: 50%;
						left: 0;
						will-change: transform;
						transform: translateY(-50%);
						text-align: center;
						display: block;
						width: 38px;
						height: 38px;
						border-radius: 50%;
						border: 5px solid $color-gray;
						font-size: 18px;
						line-height: 28px;
						transition: all 0.5s linear;
					}

					&:after {
						will-change: opacity;
						transition: opacity 0.5s linear;
						opacity: 0;
						content: "";
						display: block;
					}

					&:first-child,
					&:last-child {
						opacity: 0;
					}

					&.active {
						&:before {
							border-color: $color-tertiary;
							content: "";
							text-align: center;
						}

						&:after {
							content: "";
							opacity: 1;
							display: block;
							width: 9px;
							height: 18px;
							border: solid $color-tertiary;
							border-width: 0 4px 4px 0;
							transform: rotate(45deg);
							z-index: 6;
							position: absolute;
							top: 14px;
							left: 14px;
						}
					}

					&.has-error {
						&:before {
							border-color: $color-primary !important;
							animation: pulse-error 1.5s ease-in-out;
						}

						&:after {
							content: "!" !important;
							opacity: 1 !important;
							display: block !important;
							z-index: 6;
							position: absolute;
							top: 6px;
							left: 14px;
							font-size: 20px;
							font-weight: 700;
							color: $color-primary;
							border: none !important;
							width: auto !important;
							height: auto !important;
							transform: none !important;
						}

						& span {
							color: $color-primary;
							font-weight: 600;
						}
					}

					&.disabled {
						cursor: initial;

						&:after {
							content: "\00d7";
							opacity: 1;
							z-index: 6;
							position: absolute;
							top: -4px;
							left: 9px;
							font-size: 38px;
							color: $color-gray;
						}

						& span {
							color: $color-gray;
						}
					}

					.inventory-header-status-title {
						color: $color-gray-dark;
						position: absolute;
						white-space: nowrap;
						bottom: -24px;
						left: 50%;
						transform: translateX(-50%);
					}
				}
			}
		}
	}

	&-form {
		.form-input {
			label {
				width: 25%;
			}
		}

		&-dates {
			display: flex;
			flex-flow: row nowrap;
			align-items: center;
			width: 100%;

			.form-input {
				&:last-child {
					margin-left: 2rem;
				}

				label {
					width: 100%;
				}

				.v-text-field {
					max-width: 16rem;
				}
			}
		}
	}
}

@keyframes pulse-error {
	0% {
		box-shadow: 0 0 0 0 rgba($color-primary, 0.4);
	}
	70% {
		box-shadow: 0 0 0 8px rgba($color-primary, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba($color-primary, 0);
	}
}
</style>
